import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import ModalShell from "./ModalShell";
import { fmtNum } from "./wmsUtils";

// Aug-2026 (User-Wunsch): Quelle jeder Lagerbewegung sichtbar machen.
const SOURCE_LABEL = {
    manuell: "Manuell", delivery_note_scan: "Lieferschein-Scan", delivery_note_backorder: "Rückstand",
    rechnung: "Rechnung (Verkauf)", auftrag: "Werkstatt-Auftrag", auftrag_storno: "Auftrag-Storno",
    bestellung: "Bestellung", bestellliste: "Bestellliste", inventur: "Inventur",
    transfer: "Umlagerung", umbuchung: "Umbuchung", negative_fix: "Bestands-Korrektur",
};
const ORIGIN_SUFFIX = { inbox: "Posteingang", scan_page: "Scan-Seite" };

export default function MovementsModal({ article, suppliers, onClose, onBooked }) {
    const [movements, setMovements] = useState([]);
    const [type, setType] = useState("in");
    const [qty, setQty] = useState("");
    const [newQty, setNewQty] = useState("");
    const [unitCost, setUnitCost] = useState("");
    const [supId, setSupId] = useState(article?.supplier_id || "");
    const [reference, setReference] = useState("");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [stock, setStock] = useState(Number(article?.stock_qty || 0));
    const [locId, setLocId] = useState("");        // Lagerplatz für die Buchung
    const [locations, setLocations] = useState([]); // Lagerplätze des Standorts

    useEffect(() => {
        let on = true;
        api.get("/dealer/storage-locations")
            .then((r) => { if (on) setLocations(Array.isArray(r.data) ? r.data : (r.data?.items || [])); })
            .catch(() => {});
        return () => { on = false; };
    }, []);

    const load = useCallback(async () => {
        try {
            const res = await api.get(`/articles/${article.id}/movements`);
            setMovements(res.data || []);
            const a = await api.get(`/articles/${article.id}`);
            setStock(Number(a.data.stock_qty || 0));
        } catch (e) { /* ignore */ }
    }, [article.id]);
    useEffect(() => { load(); }, [load]);

    const book = async (e) => {
        e.preventDefault();
        const payload = {
            movement_type: type,
            quantity: Number(qty) || 0,
            reference: reference || null,
            note: note || null,
            supplier_id: supId || null,
        };
        if (type === "inventory") {
            if (newQty === "") { toast.error("Bei Inventur: neuen Bestand eingeben"); return; }
            payload.new_qty = Number(newQty);
            payload.quantity = 0;
        }
        if (type === "in" && unitCost !== "") payload.unit_cost_net = Number(unitCost);
        if (locId) payload.location_id = locId;
        if (type === "out" && (!Number(qty) || Number(qty) <= 0)) { toast.error("Menge > 0"); return; }
        if (type === "in" && (!Number(qty) || Number(qty) <= 0)) { toast.error("Menge > 0"); return; }
        setBusy(true);
        try {
            await api.post(`/articles/${article.id}/movements`, payload);
            toast.success("Buchung gespeichert");
            setQty(""); setNewQty(""); setUnitCost(""); setReference(""); setNote("");
            await load();
            onBooked();
        } catch (e) { toast.error(e.response?.data?.detail || "Buchung fehlgeschlagen"); }
        setBusy(false);
    };

    const supById = Object.fromEntries(suppliers.map((s) => [s.id, s]));

    return (
        <ModalShell onClose={onClose} title={`Lager: ${article.name}`} wide testid="movements-modal">
            <div className="space-y-4">
                <div className="bg-[#111827] text-white rounded-sm p-3 flex justify-between items-center">
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-gray-400">Aktueller Bestand</div>
                        <div className="font-display font-black text-3xl">{fmtNum(stock)} <span className="text-sm font-normal text-gray-400">{article.unit || "Stück"}</span></div>
                    </div>
                    <div className="text-right">
                        <div className="text-[10px] uppercase tracking-wider text-gray-400">SKU</div>
                        <div className="font-mono-num font-bold">{article.sku}</div>
                    </div>
                </div>

                <form onSubmit={book} className="bg-gray-50 border border-gray-200 rounded-sm p-3 space-y-2">
                    <div className="text-xs font-bold uppercase tracking-wider text-gray-700">Neue Buchung</div>
                    <div className="grid grid-cols-2 gap-2">
                        <Select value={type} onValueChange={setType}>
                            <SelectTrigger className="rounded-sm" data-testid="mov-type"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="in">Zugang (Wareneingang)</SelectItem>
                                <SelectItem value="out">Abgang (Verbrauch / Verkauf)</SelectItem>
                                <SelectItem value="inventory">Inventur (absolut setzen)</SelectItem>
                                <SelectItem value="correction">Korrektur (mit Vorzeichen)</SelectItem>
                            </SelectContent>
                        </Select>
                        {type === "inventory" ? (
                            <Input type="number" step="0.01" placeholder="Gezählter Bestand" value={newQty} onChange={(e) => setNewQty(e.target.value)} className="rounded-sm" data-testid="mov-newqty" />
                        ) : (
                            <Input type="number" step="0.01" placeholder={type === "correction" ? "Delta (+/-)" : "Menge"} value={qty} onChange={(e) => setQty(e.target.value)} className="rounded-sm" data-testid="mov-qty" />
                        )}
                    </div>
                    {type === "in" && (
                        <div className="grid grid-cols-2 gap-2">
                            <Input type="number" min="0" step="0.01" placeholder="Stk-Preis EK netto (optional)" value={unitCost} onChange={(e) => setUnitCost(e.target.value)} className="rounded-sm" data-testid="mov-cost" />
                            <Select value={supId || "any"} onValueChange={(v) => setSupId(v === "any" ? "" : v)}>
                                <SelectTrigger className="rounded-sm"><SelectValue placeholder="Lieferant…" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="any">— ohne Lieferant —</SelectItem>
                                    {suppliers.filter((s) => s.is_active).map((s) => (
                                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-2">
                        <Input placeholder="Beleg-Nr (Lieferschein / Auftrag)" value={reference} onChange={(e) => setReference(e.target.value)} className="rounded-sm" data-testid="mov-ref" />
                        <Input placeholder="Notiz" value={note} onChange={(e) => setNote(e.target.value)} className="rounded-sm" />
                    </div>
                    {locations.length > 0 && (
                        <Select value={locId || "none"} onValueChange={(v) => setLocId(v === "none" ? "" : v)}>
                            <SelectTrigger className="rounded-sm" data-testid="mov-location"><SelectValue placeholder="Lagerplatz…" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="none">— ohne Lagerplatz —</SelectItem>
                                {locations.map((l) => (
                                    <SelectItem key={l.id} value={l.id}>{l.code}{l.name ? ` · ${l.name}` : ""}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                    <Button type="submit" disabled={busy} className="w-full bg-[#111827] hover:bg-black text-white font-bold rounded-sm" data-testid="mov-submit">
                        {busy ? "Buche…" : "Buchung speichern"}
                    </Button>
                </form>

                <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">Historie ({movements.length})</div>
                    {movements.length === 0 ? (
                        <div className="text-sm text-gray-500 text-center py-4">Noch keine Bewegungen.</div>
                    ) : (
                        <div className="max-h-[300px] overflow-y-auto border border-gray-200 rounded-sm">
                            <table className="w-full text-xs">
                                <thead className="bg-gray-50 sticky top-0">
                                    <tr className="text-left uppercase tracking-wider text-[10px] text-gray-600">
                                        <th className="px-2 py-2 font-bold">Datum</th>
                                        <th className="px-2 py-2 font-bold">Typ</th>
                                        <th className="px-2 py-2 text-right font-bold">Δ</th>
                                        <th className="px-2 py-2 text-right font-bold">Stand</th>
                                        <th className="px-2 py-2 font-bold">Quelle</th>
                                        <th className="px-2 py-2 font-bold">Beleg / Notiz</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {movements.map((m) => (
                                        <tr key={m.id} className="border-t border-gray-100">
                                            <td className="px-2 py-1.5 text-gray-600 font-mono-num">{(m.created_at || "").slice(0, 16).replace("T", " ")}</td>
                                            <td className="px-2 py-1.5">
                                                <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 rounded-sm ${m.movement_type === "in" ? "bg-green-100 text-green-800" : m.movement_type === "out" ? "bg-red-100 text-red-800" : "bg-blue-100 text-blue-800"}`}>
                                                    {m.movement_type === "in" ? "Zug." : m.movement_type === "out" ? "Abg." : m.movement_type === "inventory" ? "Inv." : "Korr."}
                                                </span>
                                            </td>
                                            <td className={`px-2 py-1.5 text-right font-mono-num font-bold ${m.delta > 0 ? "text-green-700" : m.delta < 0 ? "text-red-700" : ""}`}>
                                                {m.delta > 0 ? "+" : ""}{fmtNum(m.delta)}
                                            </td>
                                            <td className="px-2 py-1.5 text-right font-mono-num">{fmtNum(m.qty_after)}</td>
                                            <td className="px-2 py-1.5" data-testid={`mov-source-${m.id}`}>
                                                {m.source ? (
                                                    <span className="inline-flex text-[9px] font-bold uppercase tracking-wider bg-gray-100 text-gray-700 rounded-sm px-1.5 py-0.5 whitespace-nowrap">
                                                        {SOURCE_LABEL[m.source] || m.source}{m.origin && ORIGIN_SUFFIX[m.origin] ? ` · ${ORIGIN_SUFFIX[m.origin]}` : ""}
                                                    </span>
                                                ) : <span className="text-gray-300">—</span>}
                                            </td>
                                            <td className="px-2 py-1.5 text-gray-600 truncate max-w-[200px]">
                                                {m.reference && <span className="font-bold">{m.reference}</span>}
                                                {m.note && <span className="text-gray-400"> · {m.note}</span>}
                                                {m.supplier_id && supById[m.supplier_id] && <span className="text-gray-400"> · {supById[m.supplier_id].name}</span>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </ModalShell>
    );
}
